/**
 * Web Push notification registration.
 *
 * Three entry points:
 *   - registerForWebPush(): passive refresh path called from main.js after
 *     auth bootstrap. Renews an already-opted-in device's FCM token; bails
 *     unless Notification.permission is granted AND there is a local
 *     registration on file for the current user. Never prompts.
 *   - requestEnable(): user-driven opt-in from the settings page. Calls
 *     Notification.requestPermission() and registers on grant
 *     (allowFirstTime=true bypasses the "already opted in" gate).
 *   - disablePush(): user-driven opt-out. Calls FCM deleteToken, DELETEs
 *     the server-side row, clears the local cache.
 *
 * The Firebase project / VAPID public key are public client identifiers
 * (Firebase API keys are safe to ship in client code; security is enforced
 * server-side via FCM authentication).
 */

import apiClient from './api-client.js';
import {createUuid} from './create-uuid.js';
import { getApiToken } from './tokenUtils.js';

const FIREBASE_CONFIG = {
    apiKey: 'AIzaSyC7lOki48NlUdbdmvMOzDnZFXTpTe968E0',
    authDomain: 'interpals08.firebaseapp.com',
    projectId: 'interpals08',
    storageBucket: 'interpals08.firebasestorage.app',
    messagingSenderId: '886838556226',
    appId: '1:886838556226:web:632af0bc3f9a3de1d2dfdb',
    measurementId: 'G-TLGP0KEGS9',
};

const VAPID_PUBLIC_KEY =
    'BO_90f-MLfHLS-HLKVIyOMJNy_Yd83UWefLped2e_RCcfb9ZewrJ8jajZUpg14tfttWfRDbVgRIVyb1tEWsXEB8';

const SERVICE_WORKER_URL = '/firebase-messaging-sw.js';
const DEVICE_ID_STORAGE_KEY = 'web-push-device-id';
const LAST_REGISTERED_TOKEN_KEY = 'web-push-last-token';
const REGISTRATION_REFRESH_MS = 12 * 60 * 60 * 1000;

function isLoggedIn() {
    return Boolean(getCurrentUserId());
}

function getCurrentUserId() {
    return typeof window !== 'undefined' && window.userId ? String(window.userId) : null;
}

function isSupported() {
    return (
        typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'Notification' in window
        && 'PushManager' in window
    );
}

function getOrCreateDeviceId() {
    let id = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (id) {
        return id;
    }
    id = createUuid() || 'web-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
    return id;
}

async function loadFirebaseMessaging() {
    // webpackIgnore: true keeps these as native browser dynamic imports —
    // webpack 5 can't bundle ES-module URLs and would otherwise refuse to
    // compile. Loaded straight from the Firebase CDN at runtime.
    const [appMod, messagingMod] = await Promise.all([
        import(/* webpackIgnore: true */ 'https://www.gstatic.com/firebasejs/10.13.0/firebase-app.js'),
        import(/* webpackIgnore: true */ 'https://www.gstatic.com/firebasejs/10.13.0/firebase-messaging.js'),
    ]);
    // initializeApp throws if called twice with the same '[DEFAULT]' name. Reuse
    // the existing app when registerForWebPush gets invoked more than once per
    // page (manual retries, future SPA navigations, etc.).
    const app = appMod.getApps().length ? appMod.getApp() : appMod.initializeApp(FIREBASE_CONFIG);
    return {
        messaging: messagingMod.getMessaging(app),
        getToken: messagingMod.getToken,
        deleteToken: messagingMod.deleteToken,
    };
}

async function postToken(token, userId, apiToken) {
    const deviceId = getOrCreateDeviceId();
    const body = new URLSearchParams({
        id: token,
        device_id: deviceId,
        platform: 'web',
    });

    const response = await apiClient.request('/push-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    });

    if (response.ok) {
        window.localStorage.setItem(
            LAST_REGISTERED_TOKEN_KEY,
            JSON.stringify({
                userId,
                apiToken,
                token,
                registeredAt: Date.now(),
            })
        );
        return;
    }

    if (window.Sentry && typeof window.Sentry.captureMessage === 'function') {
        window.Sentry.captureMessage(`web-push: /v1/push-token returned ${response.status}`);
    }
    const err = new Error(`push-token registration failed: ${response.status}`);
    err.status = response.status;
    throw err;
}

function getLastRegistration() {
    const raw = window.localStorage.getItem(LAST_REGISTERED_TOKEN_KEY);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (
            parsed
            && typeof parsed === 'object'
            && typeof parsed.userId === 'string'
            && typeof parsed.apiToken === 'string'
            && typeof parsed.token === 'string'
            && typeof parsed.registeredAt === 'number'
        ) {
            return parsed;
        }
    } catch (e) {
        return null;
    }
    return null;
}

function hasFreshRegistration(userId, apiToken) {
    const lastRegistration = getLastRegistration();
    return Boolean(
        lastRegistration
        && lastRegistration.userId === userId
        && lastRegistration.apiToken === apiToken
        && Date.now() - lastRegistration.registeredAt < REGISTRATION_REFRESH_MS
    );
}

export async function registerForWebPush({ allowFirstTime = false } = {}) {
    if (!isSupported() || !isLoggedIn()) {
        return;
    }
    if (Notification.permission !== 'granted') {
        // This refresh path only runs for existing notification grants.
        return;
    }

    const userId = getCurrentUserId();
    const apiToken = await getApiToken();
    if (!userId || !apiToken) {
        if (window.Sentry && typeof window.Sentry.captureMessage === 'function') {
            window.Sentry.captureMessage('web-push: API token unavailable');
        }
        return;
    }
    // The page-load refresh path (allowFirstTime=false) only renews an
    // existing registration. A missing local cache means the user has not
    // opted in on this device (or has explicitly opted out via settings) —
    // do not silently re-register on their behalf.
    const lastRegistration = getLastRegistration();
    if (!allowFirstTime && (!lastRegistration || lastRegistration.userId !== userId)) {
        return;
    }
    if (hasFreshRegistration(userId, apiToken)) {
        return;
    }

    let registration;
    try {
        registration = (await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL))
            || (await navigator.serviceWorker.register(SERVICE_WORKER_URL));
    } catch (err) {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
        // The page-load refresh path stays silent on errors; the user-driven
        // path needs to surface the failure so the settings UI can react.
        if (allowFirstTime) {
            throw err;
        }
        return;
    }

    try {
        const { messaging, getToken } = await loadFirebaseMessaging();
        const token = await getToken(messaging, {
            vapidKey: VAPID_PUBLIC_KEY,
            serviceWorkerRegistration: registration,
        });
        if (token) {
            await postToken(token, userId, apiToken);
        } else if (allowFirstTime) {
            // FCM returned no token (browser blocked, push service unreachable,
            // VAPID mismatch, ...). The passive refresh path would no-op; the
            // user-driven path needs to know.
            throw new Error('push-token: FCM returned no token');
        }
    } catch (err) {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
        if (allowFirstTime) {
            throw err;
        }
    }
}

/**
 * Synchronous read of the current toggle-relevant state. Used by the
 * settings UI to decide whether to render the row and what the initial
 * toggle position should be.
 *
 * @returns {{supported: boolean, permission: 'default'|'granted'|'denied'|'unsupported', enabled: boolean}}
 */
export function getRegistrationStatus() {
    if (!isSupported()) {
        return { supported: false, permission: 'unsupported', enabled: false };
    }
    const permission = Notification.permission;
    const lastRegistration = getLastRegistration();
    const userId = getCurrentUserId();
    const enabled = Boolean(
        permission === 'granted'
        && userId
        && lastRegistration
        && lastRegistration.userId === userId
        && lastRegistration.token
    );
    return { supported: true, permission, enabled };
}

/**
 * Settings-page opt-in. Prompts the browser for Notification permission and,
 * on grant, runs the same registration as the passive refresh path.
 *
 * @returns {Promise<'granted'|'denied'|'default'|'unsupported'|'registration-failed'>}
 *          the final outcome. 'granted' means permission AND server-side
 *          registration succeeded. 'registration-failed' means permission was
 *          granted but the FCM/server registration failed (stale JWT, FCM
 *          unreachable, server 5xx, ...) — the caller should surface an error
 *          to the user since the toggle won't reflect the user's intent.
 */
export async function requestEnable() {
    if (!isSupported()) {
        return 'unsupported';
    }
    if (!isLoggedIn()) {
        return Notification.permission;
    }

    let result;
    try {
        result = await Notification.requestPermission();
    } catch (err) {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
        return Notification.permission;
    }

    if (result !== 'granted') {
        return result;
    }

    try {
        await registerForWebPush({ allowFirstTime: true });
    } catch {
        return 'registration-failed';
    }
    return 'granted';
}

/**
 * Settings-page opt-out. Best-effort: FCM deleteToken to invalidate the
 * device token on Firebase, DELETE /v1/push-token/{id} to drop the
 * server-side row, then clear local cache so the toggle starts fresh.
 * Browser-level permission is left untouched (only the user can revoke
 * that via browser settings).
 *
 * Both network calls swallow errors so an opt-out always succeeds locally.
 * If either fails, the user may receive a small number of in-flight
 * notifications from pusherd until FCM's own token invalidation propagates
 * (typically within minutes once the browser's push subscription is
 * revoked client-side).
 */
export async function disablePush() {
    if (!isSupported()) {
        return;
    }
    const lastRegistration = getLastRegistration();
    window.localStorage.removeItem(LAST_REGISTERED_TOKEN_KEY);

    if (!lastRegistration || !lastRegistration.token) {
        return;
    }

    try {
        const { messaging, deleteToken } = await loadFirebaseMessaging();
        await deleteToken(messaging);
    } catch (err) {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
    }

    try {
        await apiClient.request(
            `/push-token/${encodeURIComponent(lastRegistration.token)}`,
            { method: 'DELETE' }
        );
    } catch (err) {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
    }
}

if (typeof window !== 'undefined') {
    window.registerForWebPush = registerForWebPush;
}
